import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useI18n, type TranslationKey } from '@/lib/i18n';
import { api, ApiError } from '@/lib/api-client';
import { Badge, Button, Card, CardContent, DropdownSelect, EmptyState, ErrorState, Input, PageHeader, SkeletonRow, useConfirm } from '@/components/ui';

/**
 * Admin > Business Hours: list and full-page editor (SLA-08).
 *
 * Replaces the generic name/timezone CRUD in SettingsPages.tsx with a real
 * weekly schedule grid (day x start/end, or closed) and a holiday calendar
 * (dated entries excluded from SLA business-hours math — see Holiday model /
 * sla-calculator.ts). Modelled on the same list+editor chrome as
 * RolesAdminPages/AgentsAdminPages/SlaAdminPages.
 */

export type Holiday = { id: string; name: string; date: string };
export type BusinessHours = {
  id: string;
  name: string;
  timezone: string;
  schedule: Record<string, { start?: string; end?: string } | null>;
  isDefault: boolean;
  holidays: Holiday[];
};

const DAYS: Array<{ key: string; labelKey: TranslationKey }> = [
  { key: 'monday', labelKey: 'sla.day.monday' },
  { key: 'tuesday', labelKey: 'sla.day.tuesday' },
  { key: 'wednesday', labelKey: 'sla.day.wednesday' },
  { key: 'thursday', labelKey: 'sla.day.thursday' },
  { key: 'friday', labelKey: 'sla.day.friday' },
  { key: 'saturday', labelKey: 'sla.day.saturday' },
  { key: 'sunday', labelKey: 'sla.day.sunday' },
];

// Not exhaustive, but covers the timezones already used across the seeded
// export data plus the common ones a 14-country desk is likely to pick from.
const TIMEZONES = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Australia/Sydney',
  'UTC',
];

type TFn = ReturnType<typeof useI18n>['t'];

const problem = (error: unknown, t: TFn) =>
  error instanceof ApiError ? error.message : t('common.requestFailed');

const fieldClass =
  'h-10 w-full rounded-lg border border-line-strong bg-surface px-3 text-sm text-ink shadow-card transition-all duration-150 hover:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1';

function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  if (!message) return null;
  return <ErrorState message={message} onRetry={onRetry} />;
}

function AdminHeader({
  breadcrumb,
  title,
  subtitle,
  actions,
}: {
  breadcrumb: string;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  const { t } = useI18n();
  return (
    <PageHeader
      className="mb-0"
      breadcrumb={[{ label: t('shell.settings'), to: '/settings' }, { label: breadcrumb }]}
      title={title}
      description={subtitle}
      actions={actions}
    />
  );
}

// ─── List ─────────────────────────────────────────────────────────────────────

export function BusinessHoursListPage() {
  const { t } = useI18n();
  const { confirm } = useConfirm();
  const [items, setItems] = React.useState<BusinessHours[]>([]);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setError('');
      setItems(await api.get<BusinessHours[]>('/admin/business-hours'));
    } catch (cause) {
      setError(problem(cause, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function remove(item: BusinessHours) {
    if (!await confirm({ title: t('sla.bh.deleteTitle', { name: item.name }), description: t('sla.bh.deleteDescription'), confirmLabel: t('sla.bh.deleteConfirm') })) return;
    try {
      await api.delete(`/admin/business-hours/${item.id}`);
      await load();
    } catch (cause) {
      setError(problem(cause, t));
    }
  }

  function summarize(schedule: BusinessHours['schedule']): string {
    const openDays = DAYS.filter((day) => schedule[day.key]?.start && schedule[day.key]?.end);
    if (openDays.length === 0) return t('sla.bh.closedEveryDay');
    if (openDays.length === 7) return t('sla.bh.openEveryDay');
    return openDays.length === 1 ? t('sla.bh.openDaysOne') : t('sla.bh.openDaysMany', { count: openDays.length });
  }

  return (
    <div className="animate-fade-in space-y-6">
      <AdminHeader
        breadcrumb={t('nav.businessHours')}
        title={t('nav.businessHours')}
        subtitle={t('sla.bh.subtitle')}
        actions={
          <Link
            to="/settings/business-hours/new"
            className="inline-flex h-10 items-center justify-center rounded-lg bg-brand-600 px-4 text-sm font-medium text-white shadow-card transition-all duration-150 hover:bg-brand-700 hover:shadow-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            {t('sla.bh.newCalendar')}
          </Link>
        }
      />
      <ErrorBanner message={error} onRetry={() => void load()} />

      <Card className="overflow-hidden">
        <CardContent className="divide-y divide-line p-0">
          {loading && <SkeletonRow rows={3} />}
          {!loading && items.length === 0 && (
            <EmptyState
              title={t('sla.bh.emptyTitle')}
              description={t('sla.bh.emptyDescription')}
            />
          )}
          {items.map((item) => (
            <div
              key={item.id}
              className="group flex flex-wrap items-center justify-between gap-4 p-5 transition-colors duration-150 hover:bg-brand-50/50"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/settings/business-hours/${item.id}`}
                    className="font-semibold text-brand-700 transition-colors hover:text-brand-800 hover:underline"
                  >
                    {item.name}
                  </Link>
                  {item.isDefault && <Badge>{t('sla.bh.accountDefault')}</Badge>}
                </div>
                <p className="mt-1 text-sm text-ink-muted">
                  {item.timezone} · {summarize(item.schedule)} · {item.holidays.length === 1 ? t('sla.bh.holidayOne') : t('sla.bh.holidayMany', { count: item.holidays.length })}
                </p>
              </div>

              <div className="flex gap-2 opacity-80 transition-opacity duration-150 group-hover:opacity-100">
                <Link
                  to={`/settings/business-hours/${item.id}`}
                  className="inline-flex h-8 items-center justify-center rounded-lg border border-line-strong bg-surface px-3 text-xs font-medium text-ink shadow-card transition-all duration-150 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                >
                  {t('sla.edit')}
                </Link>
                <Button size="sm" variant="destructive" onClick={() => void remove(item)}>
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Editor ───────────────────────────────────────────────────────────────────

type DaySchedule = { closed: boolean; start: string; end: string };
type WeekSchedule = Record<string, DaySchedule>;

function defaultWeekSchedule(): WeekSchedule {
  const week = {} as WeekSchedule;
  for (const day of DAYS) {
    week[day.key] = day.key === 'saturday' || day.key === 'sunday'
      ? { closed: true, start: '09:00', end: '18:00' }
      : { closed: false, start: '09:00', end: '18:00' };
  }
  return week;
}

function scheduleFromApi(raw: BusinessHours['schedule']): WeekSchedule {
  const week = defaultWeekSchedule();
  for (const day of DAYS) {
    const entry = raw[day.key];
    week[day.key] = entry?.start && entry?.end
      ? { closed: false, start: entry.start, end: entry.end }
      : { closed: true, start: week[day.key].start, end: week[day.key].end };
  }
  return week;
}

function scheduleToApi(week: WeekSchedule): Record<string, { start: string; end: string } | null> {
  const raw: Record<string, { start: string; end: string } | null> = {};
  for (const day of DAYS) {
    raw[day.key] = week[day.key].closed ? null : { start: week[day.key].start, end: week[day.key].end };
  }
  return raw;
}

export function BusinessHoursEditPage() {
  const { t, language } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [name, setName] = React.useState('');
  const [timezone, setTimezone] = React.useState('Asia/Kolkata');
  const [isDefault, setIsDefault] = React.useState(false);
  const [week, setWeek] = React.useState<WeekSchedule>(defaultWeekSchedule());
  const [holidays, setHolidays] = React.useState<Holiday[]>([]);
  const [holidayName, setHolidayName] = React.useState('');
  const [holidayDate, setHolidayDate] = React.useState('');

  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (isNew) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        setError('');
        const existing = await api.get<BusinessHours>(`/admin/business-hours/${id}`);
        if (cancelled) return;
        setName(existing.name);
        setTimezone(existing.timezone);
        setIsDefault(existing.isDefault);
        setWeek(scheduleFromApi(existing.schedule));
        setHolidays(existing.holidays);
      } catch (cause) {
        if (!cancelled) setError(problem(cause, t));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, t]);

  function updateDay(dayKey: string, patch: Partial<DaySchedule>) {
    setSaved(false);
    setWeek((current) => ({ ...current, [dayKey]: { ...current[dayKey], ...patch } }));
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      setError('');
      setSaved(false);
      const payload = { name, timezone, schedule: scheduleToApi(week), isDefault };
      if (isNew) {
        const created = await api.post<BusinessHours>('/admin/business-hours', payload);
        navigate(`/settings/business-hours/${created.id}`, { replace: true });
      } else {
        const updated = await api.put<BusinessHours>(`/admin/business-hours/${id}`, payload);
        setHolidays(updated.holidays);
      }
      setSaved(true);
    } catch (cause) {
      setError(problem(cause, t));
    } finally {
      setSaving(false);
    }
  }

  async function addHoliday(event: React.FormEvent) {
    event.preventDefault();
    if (isNew || !holidayName || !holidayDate) return;
    try {
      setError('');
      const holiday = await api.post<Holiday>(`/admin/business-hours/${id}/holidays`, {
        name: holidayName,
        date: holidayDate,
      });
      setHolidays((current) => [...current, holiday].sort((a, b) => a.date.localeCompare(b.date)));
      setHolidayName('');
      setHolidayDate('');
    } catch (cause) {
      setError(problem(cause, t));
    }
  }

  async function removeHoliday(holiday: Holiday) {
    if (isNew) return;
    try {
      setError('');
      await api.delete(`/admin/business-hours/${id}/holidays/${holiday.id}`);
      setHolidays((current) => current.filter((item) => item.id !== holiday.id));
    } catch (cause) {
      setError(problem(cause, t));
    }
  }

  return (
    <div className="animate-fade-in space-y-6">
      <AdminHeader
        breadcrumb={t('nav.businessHours')}
        title={isNew ? t('sla.bh.newTitle') : t('sla.bh.editTitle')}
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => navigate('/settings/business-hours')}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="business-hours-form" disabled={saving || loading}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </>
        }
      />
      <ErrorBanner message={error} />
      {saved && (
        <div role="status" className="animate-fade-in rounded-xl border border-success-500/20 bg-success-50 px-4 py-3 text-sm text-success-700">
          {t('sla.bh.saved')}
        </div>
      )}

      <form id="business-hours-form" onSubmit={save}>
        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
              <label htmlFor="bh-name" className="text-sm font-semibold text-ink">
                {t('sla.edit.name')} <span className="text-accent-500">*</span>
              </label>
              <Input id="bh-name" required value={name} onChange={(event) => setName(event.target.value)} />
            </div>

            <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
              <label htmlFor="bh-timezone" className="text-sm font-semibold text-ink">{t('sla.bh.timezone')}</label>
              <DropdownSelect
                options={[
                  ...TIMEZONES.map((value) => ({ value, label: value })),
                  ...(!TIMEZONES.includes(timezone) ? [{ value: timezone, label: timezone }] : []),
                ]}
                value={timezone}
                onChange={(value) => setTimezone(value)}
              />
            </div>

            <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
              <span className="text-sm font-semibold text-ink">{t('sla.bh.defaultCalendar')}</span>
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-line-strong text-brand-600 transition focus:ring-2 focus:ring-brand-500"
                  checked={isDefault}
                  onChange={(event) => setIsDefault(event.target.checked)}
                />
                {t('sla.bh.useAsDefault')}
              </label>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardContent className="space-y-4 p-6">
            <h2 className="border-b border-line pb-3 text-lg font-semibold text-ink">{t('sla.bh.weeklySchedule')}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-ink-subtle">
                    <th className="py-2 pr-4 font-medium">{t('sla.bh.colDay')}</th>
                    <th className="py-2 pr-4 font-medium">{t('sla.bh.colOpen')}</th>
                    <th className="py-2 pr-4 font-medium">{t('sla.bh.colStart')}</th>
                    <th className="py-2 font-medium">{t('sla.bh.colEnd')}</th>
                  </tr>
                </thead>
                <tbody>
                  {DAYS.map((day) => {
                    const value = week[day.key];
                    const dayLabel = t(day.labelKey);
                    return (
                      <tr key={day.key} className="border-t border-line">
                        <td className="py-2 pr-4 font-medium text-ink">{dayLabel}</td>
                        <td className="py-2 pr-4">
                          <input
                            type="checkbox"
                            aria-label={t('sla.bh.dayOpenAria', { day: dayLabel })}
                            className="h-4 w-4 rounded border-line-strong text-brand-600 transition focus:ring-2 focus:ring-brand-500"
                            checked={!value.closed}
                            onChange={(event) => updateDay(day.key, { closed: !event.target.checked })}
                          />
                        </td>
                        <td className="py-2 pr-4">
                          <input
                            type="time"
                            aria-label={t('sla.bh.dayStartAria', { day: dayLabel })}
                            disabled={value.closed}
                            className={`${fieldClass} disabled:opacity-40`}
                            value={value.start}
                            onChange={(event) => updateDay(day.key, { start: event.target.value })}
                          />
                        </td>
                        <td className="py-2">
                          <input
                            type="time"
                            aria-label={t('sla.bh.dayEndAria', { day: dayLabel })}
                            disabled={value.closed}
                            className={`${fieldClass} disabled:opacity-40`}
                            value={value.end}
                            onChange={(event) => updateDay(day.key, { end: event.target.value })}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </form>

      <Card>
        <CardContent className="space-y-4 p-6">
          <h2 className="border-b border-line pb-3 text-lg font-semibold text-ink">{t('sla.bh.holidayCalendar')}</h2>
          {isNew ? (
            <p className="text-sm text-ink-subtle">{t('sla.bh.saveFirst')}</p>
          ) : (
            <>
              <form className="flex flex-wrap items-end gap-3" onSubmit={addHoliday}>
                <Input
                  label={t('sla.bh.holidayName')}
                  required
                  value={holidayName}
                  onChange={(event) => setHolidayName(event.target.value)}
                  className="max-w-xs"
                />
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink-muted">{t('sla.bh.date')}</span>
                  <input
                    type="date"
                    required
                    className={fieldClass}
                    value={holidayDate}
                    onChange={(event) => setHolidayDate(event.target.value)}
                  />
                </label>
                <Button type="submit" disabled={!holidayName || !holidayDate}>
                  {t('sla.bh.addHoliday')}
                </Button>
              </form>

              {holidays.length === 0 ? (
                <EmptyState
                  title={t('sla.bh.noHolidays')}
                  description={t('sla.bh.noHolidaysHint')}
                />
              ) : (
                <ul className="divide-y divide-line rounded-lg border border-line">
                  {holidays.map((holiday) => (
                    <li key={holiday.id} className="flex items-center justify-between gap-4 p-3 text-sm">
                      <span className="font-medium text-ink">{holiday.name}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-ink-muted">{new Date(holiday.date).toLocaleDateString(language)}</span>
                        <Button size="sm" variant="destructive" onClick={() => void removeHoliday(holiday)}>
                          {t('sla.bh.remove')}
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
